import { type DocumentReference } from 'firebase/firestore';

import { getAppBroker } from 'stores/AppBroker';
import * as DatabasePaths from 'stores/DatabasePaths';
import { DocumentCollectionType } from 'types/DocumentCollectionType';
import { DocumentType } from 'types/DocumentType';
import { PanelViews } from 'types/PanelViews';
import { formatDateTimeLongMinutes } from 'utils/Dates';
import { nowTimestamp } from 'utils/FirestoreUtils';

import BreadcrumbManager from './BreadcrumbManager';
import { DocumentSchema } from './DocumentSchema';
import DocumentStore from './DocumentStore';
import type { RootStore } from './RootStore';
import TearsheetAdsManager from './TearsheetAdsManager';
import type Tearsheets from './Tearsheets';

export class TearsheetSchema extends DocumentSchema {
    name = '';
    archived = false;
    author = '';
    generating = false;
    dateModified = nowTimestamp();
    type = DocumentType.TEARSHEET;
}

// A survey of what actually constructs a Tearsheet, not a statement of what
// ought to. There are two paths, and they disagree:
//
//   DocumentCollection:222  `new this.childClass(this, docRef)` - the
//                           parent is the Tearsheets collection
//   RootStore.loadTearsheet `new Tearsheet(this, ...)` - the parent is the
//                           RootStore, for the standalone tearsheet view
//
// Both imports are type-only, so neither the Tearsheets nor the RootStore
// cycle becomes a runtime one.
export type TearsheetParent = Tearsheets | RootStore;

export default class Tearsheet extends DocumentStore<TearsheetSchema> {
    // TODO: tearsheet class might be incomplete still
    declare parent: TearsheetParent | undefined;

    schema = new TearsheetSchema();
    type = DocumentType.TEARSHEET;
    collectionType = DocumentCollectionType.TEARSHEET;
    displayPanel = PanelViews.NONE;

    ads = new TearsheetAdsManager(this);
    breadcrumbs: BreadcrumbManager = new BreadcrumbManager(this);
    generating = false;
    author = '';

    viewScale = 1;

    constructor(parent: TearsheetParent, docRef?: DocumentReference) {
        super(parent, docRef);
        this.becomeObservable();
    }

    get formattedDateModified() {
        return formatDateTimeLongMinutes(this.data.dateModified);
    }

    navigateTo = () => {
        const { clientID, projectID, creativeID, tearsheetID } =
            DatabasePaths.tearsheet.requireMatch(this.docRef.path);

        void getAppBroker().navigate({
            clientID,
            projectID,
            creativeID,
            tearsheetID,
            adIndex: '-',
        });
    };

    getRoute = () => {
        const { clientID, projectID, creativeID, tearsheetID } =
            DatabasePaths.tearsheet.requireMatch(this.docRef.path);

        return getAppBroker().routeForDocument({
            clientID,
            projectID,
            creativeID,
            tearsheetID,
            adIndex: '-',
        });
    };

    onLoad = async (_data: TearsheetSchema) => {
        await this.breadcrumbs.load();
        await this.ads.load();
    };

    setViewScale(scale: number) {
        this.viewScale = scale;
    }

    // FIXME this replaces DocumentStore.reset() outright rather than
    // extending it - there is no super.reset(), so onReset() never runs and
    // none of the base class's state is reset. Only the disposers registered
    // by DocumentStore.load() are cleared.
    reset() {
        this.disposer.clear();
    }
}
